import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Select, DatePicker, Input, InputNumber, Upload, message } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Expense, ExpenseFormData, ExpenseCategory } from '../../types/expense';
import { expenseService } from '../../services/expenseService';
import { supplierService } from '../../services/supplierService';
import { Supplier } from '../../services/supplierService';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface ExpenseModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  expense?: Expense;
}

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  visible,
  onClose,
  onSuccess,
  expense
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  useEffect(() => {
    if (visible) {
      loadCategories();
      loadSuppliers();
      if (expense) {
        form.setFieldsValue({
          ...expense,
          expense_date: dayjs(expense.expense_date),
          category_id: expense.category_id,
          supplier_id: expense.supplier_id
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, expense, form]);

  const loadCategories = async () => {
    try {
      const data = await expenseService.getCategories();
      setCategories(data);
    } catch (error) {
      message.error('Không thể tải danh mục chi phí');
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      message.error('Không thể tải danh sách nhà cung cấp');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const formData: ExpenseFormData = {
        ...values,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        category_id: values.category_id,
        supplier_id: values.supplier_id || null,
        payment_status: values.payment_status || 'pending'
      };

      if (expense) {
        await expenseService.updateExpense(expense.id, formData);
        message.success('Cập nhật chi phí thành công');
      } else {
        // Đảm bảo payment_status được set khi tạo mới
        const createData = {
          ...formData,
          payment_status: formData.payment_status || 'pending'
        };
        await expenseService.createExpense(createData);
        message.success('Tạo chi phí thành công');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      message.error('Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={expense ? 'Chỉnh sửa chi phí' : 'Thêm chi phí mới'}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          payment_method: 'cash',
          payment_status: expense?.payment_status || 'paid',
          amount: 0,
          expense_date: expense ? undefined : dayjs()
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="title"
              label="Tiêu đề"
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
            >
              <Input placeholder="Nhập tiêu đề chi phí" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="category_id"
              label="Danh mục"
              rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
            >
              <Select placeholder="Chọn danh mục">
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="amount"
              label="Số tiền"
              rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0"
                min={0}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expense_date"
              label="Ngày chi phí"
              rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="payment_method"
              label="Phương thức thanh toán"
            >
              <Select>
                <Option value="cash">Tiền mặt</Option>
                <Option value="bank_transfer">Chuyển khoản</Option>
                <Option value="credit_card">Thẻ tín dụng</Option>
                <Option value="other">Khác</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="supplier_id"
              label="Nhà cung cấp"
            >
              <Select placeholder="Chọn nhà cung cấp" allowClear>
                {suppliers.map(supplier => (
                  <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="invoice_number"
              label="Số hóa đơn"
            >
              <Input placeholder="Nhập số hóa đơn" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="payment_status"
              label="Trạng thái thanh toán"
            >
              <Select>
                <Option value="pending">Chờ thanh toán</Option>
                <Option value="paid">Đã thanh toán</Option>
                <Option value="cancelled">Đã hủy</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Mô tả"
        >
          <TextArea rows={3} placeholder="Mô tả chi tiết chi phí" />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Ghi chú"
        >
          <TextArea rows={2} placeholder="Ghi chú bổ sung" />
        </Form.Item>

        <Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Button onClick={onClose} style={{ marginRight: 8 }}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {expense ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExpenseModal;
